import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRecentProjectIds } from '@/lib/recentProjects';

interface ServerRecentProject {
  projectId: string;
  accessedAt: string;
}

/**
 * Custom hook that provides reactive recent projects.
 *
 * Source of truth priority:
 *   1. Server (cross-device, persisted) — fetched via React Query
 *   2. localStorage (optimistic same-device fallback while server loads)
 *
 * Listens to the custom 'recentProjectsUpdated' event so that clicking a
 * project on the same tab updates the list immediately, before the server
 * query re-fetches.
 */
export function useRecentProjects<T extends { id: string }>(
  projects: T[],
  userId: string | undefined,
  limit: number = 3
): T[] {
  const queryClient = useQueryClient();

  // Track localStorage version so useMemo recomputes when it changes
  const [storageVersion, setStorageVersion] = useState(0);

  // Re-trigger on userId becoming available
  useEffect(() => {
    if (userId) setStorageVersion(Date.now());
  }, [userId]);

  // Listen for same-tab project access events to refresh immediately
  useEffect(() => {
    if (!userId) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `recentProjects_${userId}` || e.key === null) {
        setStorageVersion(Date.now());
        queryClient.invalidateQueries({ queryKey: ['/api/users/me/recent-projects'] });
      }
    };

    const handleRecentUpdate = ((e: CustomEvent) => {
      if (e.detail?.userId === userId) {
        setStorageVersion(Date.now());
        queryClient.invalidateQueries({ queryKey: ['/api/users/me/recent-projects'] });
      }
    }) as EventListener;

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('recentProjectsUpdated', handleRecentUpdate);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('recentProjectsUpdated', handleRecentUpdate);
    };
  }, [userId, queryClient]);

  // Fetch server-side recent projects (cross-device source of truth)
  const { data: serverRecent } = useQuery<ServerRecentProject[]>({
    queryKey: ['/api/users/me/recent-projects'],
    queryFn: async () => {
      const res = await fetch('/api/users/me/recent-projects', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch recent projects');
      return res.json();
    },
    enabled: !!userId,
    staleTime: 1000 * 30, // 30 seconds — refresh in background if stale
    retry: false,
  });

  return useMemo(() => {
    if (!userId || projects.length === 0) return [];

    const projectMap = new Map(projects.map(p => [p.id, p]));

    // Prefer server data (cross-device), fall back to localStorage while loading
    let orderedIds: string[];

    if (serverRecent && serverRecent.length > 0) {
      orderedIds = serverRecent.map(r => r.projectId);
    } else {
      // localStorage fallback — storageVersion triggers recompute on updates
      orderedIds = getRecentProjectIds(userId);
      void storageVersion; // explicitly reference to keep dep array happy
    }

    return orderedIds
      .map(id => projectMap.get(id))
      .filter((p): p is T => p !== undefined)
      .slice(0, limit);
  }, [projects, userId, limit, serverRecent, storageVersion]);
}
